import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowDropDownRounded from '@mui/icons-material/ArrowDropDownRounded'
import { Box, Divider, Menu, MenuItem, Typography } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { motivationColor } from '../../helpers/motivationColors'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { TermRef } from '../../types'

interface LensDropdownProps {
  /** Active motivation lens, or null for "all creatives". */
  currentLens: TermRef | null
  /** Motivations available as lenses for the current pattern. */
  options: TermRef[]
  /** Called with the selected motivation id, or null to clear the lens. */
  onSelect: (motivationId: string | null) => void
}

/**
 * Lens selector shown on the Story Pattern detail view: lets the user view the
 * pattern's example creatives through a specific motivation (or clear the lens).
 */
export const LensDropdown: FC<LensDropdownProps> = ({ currentLens, options, onSelect }) => {
  const { t } = useTranslation('ua-insights')
  const { motivationLabel } = useUATaxonomy()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const handleSelect = (motivationId: string | null) => {
    setAnchorEl(null)
    onSelect(motivationId)
  }

  return (
    <>
      <GRTooltip content={t('showcase_lens_tooltip')}>
        <Box
          component="button"
          type="button"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.6875rem',
            fontWeight: 700,
            px: 1.25,
            py: 0.5,
            borderRadius: 100,
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
          }}
        >
          {currentLens && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: motivationColor(currentLens) }} />}
          {t('showcase_lens_label', { lens: currentLens ? motivationLabel(currentLens) : t('showcase_lens_all') })}
          <ArrowDropDownRounded fontSize="small" />
        </Box>
      </GRTooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} MenuListProps={{ dense: true }}>
        <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', px: 2, py: 0.5 }}>
          {t('showcase_lens_menu_label')}
        </Typography>
        <MenuItem selected={!currentLens} onClick={() => handleSelect(null)}>
          {t('showcase_lens_none')}
        </MenuItem>
        <Divider />
        {options.map((option) => (
          <MenuItem key={option.id} selected={option.id === currentLens?.id} onClick={() => handleSelect(option.id)} sx={{ gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: motivationColor(option) }} />
            {motivationLabel(option)}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
